import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CATEGORY_OPTIONS = [
  { value: 'sales', label: 'Sales' },
  { value: 'labor', label: 'Labor' },
  { value: 'tips', label: 'Tips' },
  { value: 'cogs', label: 'COGS' },
  { value: 'other', label: 'Other' },
];

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const VALUE_FORMAT_OPTIONS = [
  { value: 'dollars', label: 'Dollars ($)' },
  { value: 'hours', label: 'Hours (hrs)' },
  { value: 'number', label: 'Number' },
];

const EMPTY_FORM = {
  categoryKey: 'sales',
  name: '',
  sourceKey: '',
  database: '',
  schema: '',
  table: '',
  dateColumn: '',
  storeColumn: 'STORE_ID',
  valueColumnName: '',
  valueColumn: '',
  valueFormat: 'dollars',
  procedureName: '',
  procedureArgs: '{START_DATE},{END_DATE}',
  sortOrder: 0,
  expectedDays: '',
  description: '',
  dataLagDays: '',
};

const DAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const ManageSourcesDialog = ({ open, onClose, firebaseUser }) => {
  const [sources, setSources] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);

  // Form state
  const [view, setView] = useState('table'); // 'table' | 'form'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Table picker state
  const [databases, setDatabases] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [tables, setTables] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [orderBy, setOrderBy] = useState('categoryKey');
  const [orderDir, setOrderDir] = useState('asc');
  const [filters, setFilters] = useState({ categoryKey: '', name: '', tableName: '', procedureName: '' });

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrderDir('asc');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const filteredSources = sources.filter((src) => {
    if (filters.categoryKey && src.categoryKey !== filters.categoryKey) return false;
    if (filters.name && !(src.name || '').toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.tableName && !(src.tableName || '').toLowerCase().includes(filters.tableName.toLowerCase())) return false;
    if (filters.procedureName === '_has') {
      if (!src.procedureName) return false;
    } else if (filters.procedureName === '_none') {
      if (src.procedureName) return false;
    } else if (filters.procedureName) {
      if (!(src.procedureName || '').toLowerCase().includes(filters.procedureName.toLowerCase())) return false;
    }
    return true;
  });

  const getSourceHealth = (src) => health[src.sourceKey] || {};

  const sortedSources = [...filteredSources].sort((a, b) => {
    let aVal, bVal;
    if (orderBy === 'daysAgo') {
      aVal = getSourceHealth(a).daysAgo ?? 9999;
      bVal = getSourceHealth(b).daysAgo ?? 9999;
    } else if (orderBy === 'trendPct') {
      aVal = getSourceHealth(a).trendPct ?? -9999;
      bVal = getSourceHealth(b).trendPct ?? -9999;
    } else {
      aVal = (a[orderBy] ?? '').toString().toLowerCase();
      bVal = (b[orderBy] ?? '').toString().toLowerCase();
    }
    if (aVal < bVal) return orderDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return orderDir === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const headers = { Authorization: `Bearer ${token}` };
      const [sourcesRes, authRes] = await Promise.all([
        axios.get('/hd-api/data-integrity-2/sources', { headers }),
        axios.get('/hd-api/data-integrity-2/procedure-sources', { headers }),
      ]);

      setSources(sourcesRes.data.sources || []);
      setAuthorized(!!authRes.data.authorized);
      setLoading(false);

      // Fetch health data in background (non-blocking)
      axios.get('/hd-api/data-integrity-2/sources/health', { headers })
        .then((res) => setHealth(res.data.health || {}))
        .catch(() => {});
    } catch (err) {
      console.error('Error fetching sources:', err);
      setError('Failed to load data sources.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && firebaseUser) {
      fetchSources();
      setView('table');
      setEditingId(null);
      setDeleteConfirmId(null);
      setFilters({ categoryKey: '', name: '', tableName: '', procedureName: '' });
    }
  }, [open]);

  // Load databases when entering form view
  useEffect(() => {
    if (view !== 'form' || !firebaseUser) return;
    let cancelled = false;
    const loadDatabases = async () => {
      setLoadingDatabases(true);
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.get('/hd-api/data-integrity-2/databases', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setDatabases(res.data.databases || []);
      } catch (err) {
        console.error('Error loading databases:', err);
        if (!cancelled) setDatabases(['THE_WAREHOUSE', 'MENDO_APP_DB']);
      } finally {
        if (!cancelled) setLoadingDatabases(false);
      }
    };
    loadDatabases();
    return () => { cancelled = true; };
  }, [view, firebaseUser]);

  // Load schemas when database changes
  useEffect(() => {
    if (!form.database || !firebaseUser) {
      setSchemas([]);
      return;
    }
    let cancelled = false;
    const loadSchemas = async () => {
      setLoadingSchemas(true);
      setSchemas([]);
      setTables([]);
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.get(`/hd-api/data-integrity-2/schemas/${form.database}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSchemas(res.data.schemas || []);
      } catch (err) {
        console.error('Error loading schemas:', err);
      } finally {
        if (!cancelled) setLoadingSchemas(false);
      }
    };
    loadSchemas();
    return () => { cancelled = true; };
  }, [form.database, firebaseUser]);

  // Load tables when schema changes
  useEffect(() => {
    if (!form.database || !form.schema || !firebaseUser) {
      setTables([]);
      return;
    }
    let cancelled = false;
    const loadTables = async () => {
      setLoadingTables(true);
      setTables([]);
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.get(`/hd-api/data-integrity-2/tables/${form.database}/${form.schema}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setTables(res.data.tables || []);
      } catch (err) {
        console.error('Error loading tables:', err);
      } finally {
        if (!cancelled) setLoadingTables(false);
      }
    };
    loadTables();
    return () => { cancelled = true; };
  }, [form.database, form.schema, firebaseUser]);

  const handleClose = () => {
    setView('table');
    setEditingId(null);
    setError(null);
    setDeleteConfirmId(null);
    onClose();
  };

  const handleAdd = () => {
    setForm({ ...EMPTY_FORM });
    setSchemas([]);
    setTables([]);
    setEditingId(null);
    setView('form');
  };

  const handleEdit = (source) => {
    // Parse tableName into database.schema.table
    const tableParts = (source.tableName || '').split('.');
    const database = tableParts[0] || '';
    const schema = tableParts[1] || '';
    const table = tableParts[2] || '';

    setForm({
      categoryKey: source.categoryKey,
      name: source.name,
      sourceKey: source.sourceKey,
      database,
      schema,
      table,
      dateColumn: source.dateColumn,
      storeColumn: source.storeColumn || 'STORE_ID',
      valueColumnName: source.valueColumnName,
      valueColumn: source.valueColumn,
      valueFormat: source.valueFormat || 'dollars',
      procedureName: source.procedureName || '',
      procedureArgs: source.procedureArgs || '{START_DATE},{END_DATE}',
      sortOrder: source.sortOrder || 0,
      expectedDays: source.expectedDays || '',
      description: source.description || '',
      dataLagDays: source.dataLagDays || '',
    });
    setEditingId(source.id);
    setView('form');
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      const token = await firebaseUser.getIdToken(true);
      await axios.delete(`/hd-api/data-integrity-2/sources/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmId(null);
      await fetchSources();
    } catch (err) {
      console.error('Error deleting source:', err);
      setError('Failed to delete source.');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      // Combine database, schema, table into fully qualified table name
      const tableName = [form.database, form.schema, form.table].filter(Boolean).join('.');

      const payload = {
        categoryKey: form.categoryKey,
        name: form.name,
        sourceKey: form.sourceKey,
        tableName,
        dateColumn: form.dateColumn,
        storeColumn: form.storeColumn || 'STORE_ID',
        valueColumnName: form.valueColumnName,
        valueColumn: form.valueColumn,
        valueFormat: form.valueFormat || 'dollars',
        procedureName: form.procedureName || null,
        procedureArgs: form.procedureName ? (form.procedureArgs || null) : null,
        sortOrder: Number(form.sortOrder) || 0,
        expectedDays: form.expectedDays || null,
        description: form.description || null,
        dataLagDays: form.dataLagDays ? Number(form.dataLagDays) : null,
      };

      if (editingId) {
        await axios.put(`/hd-api/data-integrity-2/sources/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post('/hd-api/data-integrity-2/sources', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setSaving(false);
      setView('table');
      setEditingId(null);
      await fetchSources();
    } catch (err) {
      console.error('Error saving source:', err);
      setError(err.response?.data?.error || 'Failed to save source.');
      setSaving(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-slugify source key from name when creating
      if (field === 'name' && !editingId) {
        updated.sourceKey = slugify(value);
      }
      return updated;
    });
  };

  const canSave =
    form.categoryKey &&
    form.name &&
    form.sourceKey &&
    form.database &&
    form.schema &&
    form.table &&
    form.dateColumn &&
    form.valueColumnName &&
    form.valueColumn;

  // ----- Render -----

  const renderTable = () => (
    <>
      <DialogTitle
        sx={{
          fontFamily: 'Lato',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Manage Data Sources
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {authorized && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ textTransform: 'none', fontFamily: 'Lato' }}
            >
              Add Source
            </Button>
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : sources.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No data sources found.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    { id: 'categoryKey', label: 'Category' },
                    { id: 'name', label: 'Name' },
                    { id: 'tableName', label: 'Table' },
                    { id: 'procedureName', label: 'Procedure' },
                    { id: 'daysAgo', label: 'Freshness' },
                    { id: 'trendPct', label: 'Trend' },
                  ].map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 700, fontFamily: 'Lato' }}>
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? orderDir : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  {authorized && (
                    <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Actions</TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <NativeSelect
                      value={filters.categoryKey}
                      onChange={(e) => handleFilterChange('categoryKey', e.target.value)}
                      input={<CompactInput />}
                      fullWidth
                      sx={{ minWidth: 80 }}
                    >
                      <option value="">All</option>
                      {CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </NativeSelect>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <CompactInput
                      placeholder="Filter..."
                      value={filters.name}
                      onChange={(e) => handleFilterChange('name', e.target.value)}
                      fullWidth
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <CompactInput
                      placeholder="Filter..."
                      value={filters.tableName}
                      onChange={(e) => handleFilterChange('tableName', e.target.value)}
                      fullWidth
                      sx={{ minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <NativeSelect
                      value={filters.procedureName}
                      onChange={(e) => handleFilterChange('procedureName', e.target.value)}
                      input={<CompactInput />}
                      fullWidth
                      sx={{ minWidth: 100 }}
                    >
                      <option value="">All</option>
                      <option value="_has">Has Procedure</option>
                      <option value="_none">No Procedure</option>
                    </NativeSelect>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }} />
                  <TableCell sx={{ py: 0.5, px: 1 }} />
                  {authorized && <TableCell sx={{ py: 0.5, px: 1 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSources.map((src) => (
                  <TableRow key={src.id} hover>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{src.categoryKey}</TableCell>
                    <TableCell>{src.name}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {src.tableName}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {src.procedureName || '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const h = getSourceHealth(src);
                        if (h.daysAgo == null) return <Typography variant="caption" color="text.disabled">—</Typography>;
                        const color = h.daysAgo <= 1 ? 'success' : h.daysAgo <= 3 ? 'warning' : 'error';
                        const label = h.daysAgo === 0 ? 'Today' : h.daysAgo === 1 ? '1d ago' : `${h.daysAgo}d ago`;
                        return (
                          <Tooltip title={`Latest data: ${h.latestDate}`}>
                            <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600, fontSize: 11 }} />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const h = getSourceHealth(src);
                        if (h.trendPct == null) return <Typography variant="caption" color="text.disabled">—</Typography>;
                        const sign = h.trendPct > 0 ? '+' : '';
                        const color = Math.abs(h.trendPct) <= 15 ? 'success' : Math.abs(h.trendPct) <= 30 ? 'warning' : 'error';
                        return (
                          <Tooltip title="vs 7-day avg">
                            <Chip label={`${sign}${h.trendPct}%`} size="small" color={color} variant="outlined" sx={{ fontWeight: 600, fontSize: 11 }} />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    {authorized && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {deleteConfirmId === src.id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => handleDelete(src.id)}
                              sx={{ textTransform: 'none', fontSize: 11, py: 0, minWidth: 0, px: 1 }}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setDeleteConfirmId(null)}
                              sx={{ textTransform: 'none', fontSize: 11, py: 0, minWidth: 0, px: 1 }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(src)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeleteConfirmId(src.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </>
  );

  const renderForm = () => (
    <>
      <DialogTitle
        sx={{
          fontFamily: 'Lato',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton size="small" onClick={() => setView('table')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        {editingId ? 'Edit Data Source' : 'Add Data Source'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Category
            </Typography>
            <NativeSelect
              value={form.categoryKey}
              onChange={(e) => handleFormChange('categoryKey', e.target.value)}
              input={<CompactInput />}
              fullWidth
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect>
          </Box>

          <TextField
            label="Name"
            size="small"
            value={form.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            fullWidth
          />

          <TextField
            label="Description (optional)"
            size="small"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            fullWidth
            helperText="Document what this data source is, when it updates, and any special notes."
          />

          <TextField
            label="Source Key"
            size="small"
            value={form.sourceKey}
            onChange={(e) => handleFormChange('sourceKey', e.target.value)}
            helperText="Slug identifier (auto-generated from name)"
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Autocomplete
              size="small"
              options={databases}
              value={form.database || null}
              onChange={(_, val) => {
                handleFormChange('database', val || '');
                handleFormChange('schema', '');
                handleFormChange('table', '');
              }}
              loading={loadingDatabases}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Database"
                  helperText=" "
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDatabases ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1 }}
              freeSolo
              autoSelect
            />
            <Autocomplete
              size="small"
              options={schemas}
              value={form.schema || null}
              onChange={(_, val) => {
                handleFormChange('schema', val || '');
                handleFormChange('table', '');
              }}
              loading={loadingSchemas}
              disabled={!form.database}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Schema"
                  helperText=" "
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSchemas ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1 }}
              freeSolo
              autoSelect
            />
            <Autocomplete
              size="small"
              options={tables}
              value={form.table || null}
              onChange={(_, val) => handleFormChange('table', val || '')}
              loading={loadingTables}
              disabled={!form.schema}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Table"
                  helperText=" "
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTables ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1.5 }}
              freeSolo
              autoSelect
            />
          </Box>

          <TextField
            label="Date Column"
            size="small"
            value={form.dateColumn}
            onChange={(e) => handleFormChange('dateColumn', e.target.value)}
            helperText="Column name or expression for date filtering"
            fullWidth
          />

          <TextField
            label="Store Column"
            size="small"
            value={form.storeColumn}
            onChange={(e) => handleFormChange('storeColumn', e.target.value)}
            helperText="Default: STORE_ID"
            fullWidth
          />

          <TextField
            label="Value Column Name"
            size="small"
            value={form.valueColumnName}
            onChange={(e) => handleFormChange('valueColumnName', e.target.value)}
            helperText="Display label for the value column"
            fullWidth
          />

          <TextField
            label="Value Column"
            size="small"
            value={form.valueColumn}
            onChange={(e) => handleFormChange('valueColumn', e.target.value)}
            helperText="Actual column name in the table"
            fullWidth
          />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Value Format
            </Typography>
            <NativeSelect
              value={form.valueFormat}
              onChange={(e) => handleFormChange('valueFormat', e.target.value)}
              input={<CompactInput />}
              fullWidth
            >
              {VALUE_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect>
          </Box>

          <TextField
            label="Procedure Name"
            size="small"
            value={form.procedureName}
            onChange={(e) => handleFormChange('procedureName', e.target.value)}
            helperText="Optional stored procedure (leave blank if none)"
            fullWidth
          />

          {form.procedureName && (
            <TextField
              label="Procedure Args"
              size="small"
              value={form.procedureArgs}
              onChange={(e) => handleFormChange('procedureArgs', e.target.value)}
              helperText="Template tokens: {START_DATE}, {END_DATE}, {STORE_ID}"
              fullWidth
            />
          )}

          <TextField
            label="Sort Order"
            size="small"
            type="number"
            value={form.sortOrder}
            onChange={(e) => handleFormChange('sortOrder', e.target.value)}
            fullWidth
          />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Expected Days (optional)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {DAY_OPTIONS.map((day) => {
                const selected = form.expectedDays
                  .split(',')
                  .filter(Boolean)
                  .map(Number)
                  .includes(day.value);
                return (
                  <Chip
                    key={day.value}
                    label={day.label}
                    size="small"
                    onClick={() => {
                      const currentDays = form.expectedDays
                        .split(',')
                        .filter(Boolean)
                        .map(Number);
                      const newDays = selected
                        ? currentDays.filter((d) => d !== day.value)
                        : [...currentDays, day.value].sort((a, b) => a - b);
                      handleFormChange('expectedDays', newDays.join(','));
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selected ? 'primary.main' : 'grey.200',
                      color: selected ? 'white' : 'text.primary',
                      '&:hover': {
                        bgcolor: selected ? 'primary.dark' : 'grey.300',
                      },
                    }}
                  />
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Leave empty if data is expected every day. Select specific days if data only arrives on certain days.
            </Typography>
          </Box>

          <TextField
            label="Data Lag Days (optional)"
            size="small"
            type="number"
            value={form.dataLagDays}
            onChange={(e) => handleFormChange('dataLagDays', e.target.value)}
            fullWidth
            helperText="If data represents a prior period, enter the number of days to look back. E.g., 7 for weekly data that arrives with last week's info."
            inputProps={{ min: 0 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => { setView('table'); setError(null); }}
          sx={{ textTransform: 'none', fontFamily: 'Lato' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
          sx={{ textTransform: 'none', fontFamily: 'Lato' }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      {view === 'table' ? renderTable() : renderForm()}
    </Dialog>
  );
};

export default ManageSourcesDialog;
